import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import {
  exportChartOfAccounts,
  todayFilenameStamp,
  type ExportableAccountNode,
} from '@/lib/export-coa';

type Scope = 'visible' | 'full';

interface ExportCoaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  visibleTree: ExportableAccountNode[];
  visibleCount: number;
}

export function ExportCoaDialog({
  open,
  onOpenChange,
  visibleTree,
  visibleCount,
}: ExportCoaDialogProps) {
  const { t } = useTranslation('tradingAccounting');
  const [scope, setScope] = useState<Scope>('visible');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [indented, setIndented] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const utils = trpc.useUtils();

  const handleExport = async () => {
    setIsExporting(true);
    try {
      let treeToExport: ExportableAccountNode[];
      if (scope === 'visible') {
        treeToExport = visibleTree;
      } else {
        treeToExport = (await utils.tradingAccount.getTree.fetch({
          isActive: includeInactive ? undefined : true,
          includeMetadata: true,
        })) as ExportableAccountNode[];
      }

      if (treeToExport.length === 0) {
        showErrorToast(t('export.empty', 'No accounts to export'));
        return;
      }

      exportChartOfAccounts(treeToExport, {
        indented,
        filename: `chart-of-accounts-${todayFilenameStamp()}.xlsx`,
      });
      showSuccessToast(t('export.success', 'Export ready'));
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unexpected error';
      showErrorToast(`${t('export.error', 'Export failed')}: ${message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const isVisibleEmpty = scope === 'visible' && visibleCount === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('export.title', 'Export Chart of Accounts')}</DialogTitle>
          <DialogDescription>
            {t(
              'export.description',
              'Download the chart of accounts as an Excel (.xlsx) file.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Scope */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t('export.scope', 'Scope')}</Label>
            <RadioGroup value={scope} onValueChange={(v) => setScope(v as Scope)}>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="visible" id="export-scope-visible" />
                <Label
                  htmlFor="export-scope-visible"
                  className="text-sm font-normal leading-tight cursor-pointer"
                >
                  {t('export.scopeVisible', 'Visible accounts only')}
                  <span className="block text-xs text-muted-foreground">
                    {t('export.scopeVisibleHint', 'Respects current search and type filter')}
                    {' '}
                    ({visibleCount})
                  </span>
                </Label>
              </div>
              <div className="flex items-start gap-2">
                <RadioGroupItem value="full" id="export-scope-full" />
                <Label
                  htmlFor="export-scope-full"
                  className="text-sm font-normal leading-tight cursor-pointer"
                >
                  {t('export.scopeFull', 'Full chart of accounts')}
                  <span className="block text-xs text-muted-foreground">
                    {t('export.scopeFullHint', 'Ignores filters, fetches every account')}
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Include inactive (only meaningful for full scope) */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="export-include-inactive"
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(checked === true)}
              disabled={scope !== 'full'}
            />
            <Label
              htmlFor="export-include-inactive"
              className={`text-sm font-normal cursor-pointer ${scope !== 'full' ? 'text-muted-foreground' : ''}`}
            >
              {t('export.includeInactive', 'Include inactive accounts')}
            </Label>
          </div>

          {/* Indent names */}
          <div className="flex items-center gap-2">
            <Checkbox
              id="export-indented"
              checked={indented}
              onCheckedChange={(checked) => setIndented(checked === true)}
            />
            <Label
              htmlFor="export-indented"
              className="text-sm font-normal cursor-pointer"
            >
              {t('export.indented', 'Indent names by hierarchy')}
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isExporting}
            className="h-8"
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleExport}
            disabled={isExporting || isVisibleEmpty}
            className="h-8"
          >
            {isExporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('export.exporting', 'Exporting…')}
              </>
            ) : (
              <>
                <Download className="mr-2 h-4 w-4" />
                {t('export.action', 'Export to Excel')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
